import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService, semesterReviewService } from "../../services";
import { useLanguage } from "../../contexts/LanguageContext";
import { useVisibility } from "../../contexts/VisibilityContext";
import LanguageSwitcher from "../common/LanguageSwitcher";
import Modal, { ModalSecondaryButton, ModalPrimaryButton } from "../common/Modal";

const TrainerSemesterReviewsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const fileInputRef = useRef(null);
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [error, setError] = useState(null);
  const [authorFilter, setAuthorFilter] = useState("all"); // "all", "trainer", "learner"
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [showExportConfig, setShowExportConfig] = useState(false);
  const [selectedReviewIds, setSelectedReviewIds] = useState([]);
  const currentUser = authService.getCurrentUser();
  const { canAccess } = useVisibility();
  const isPracticeInstructor = currentUser?.roles?.some(
    (r) => r.name === "PRAXISBILDNER" || r.name === "PRACTICE_INSTRUCTOR"
  );
  // Practice Instructors can create ONLY educational reports (Bildungsbericht),
  // and only when the visibility setting allows it. Non-PIs create freely.
  const canCreate = !isPracticeInstructor || canAccess("CREATE_EDUCATIONAL_REPORTS");
  const canViewSemesterHistory =
    !isPracticeInstructor || canAccess("VIEW_PAST_SEMESTER_REVIEWS");
  const canViewEducationalHistory =
    !isPracticeInstructor ||
    canAccess("VIEW_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS") ||
    canAccess("VIEW_PAST_EDUCATIONAL_REPORTS");
  const canLoadHistory = canViewSemesterHistory || canViewEducationalHistory;

  // Semester options state
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [selectedSemesterCode, setSelectedSemesterCode] = useState("");

  // Create dialog form state
  const [newReview, setNewReview] = useState({
    templateCode: "KOMPETENZEN",
    title: "",
  });

  const generateReviewTitle = (templateCode, semCode = selectedSemesterCode, currentLang = language) => {
    if (!selectedLearner) return "";
    
    // Find selected semester option
    const semOption = semesterOptions.find(opt => opt.semesterCode === semCode);
    const semesterLabel = semOption ? semOption.semesterCode : "";

    const learnerName = `${selectedLearner.firstName} ${selectedLearner.lastName}`;
    
    let titleType;
    if (currentLang === 'en') {
       const templateNamesEn = {
          KOMPETENZEN: "Competency Report",
          BB_LEISTUNGSBEURTEILUNG: "Performance Assessment"
       };
       titleType = templateNamesEn[templateCode] || "Semester Review";
    } else {
       const templateNamesDe = {
         KOMPETENZEN: "Bildungsbericht",
         BB_LEISTUNGSBEURTEILUNG: "Semestergespräch"
       };
       titleType = templateNamesDe[templateCode] || "Semestergespräch";
    }
    
    if (!semesterLabel) return `${learnerName} ${titleType}`; // Fallback

    return `${learnerName} ${titleType} ${semesterLabel}`;
  };

  useEffect(() => {
    loadLearners();
    // loadSemesterOptions called via selectedLearner effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

/* REMOVE OLD loadSemesterOptions */



  // Handle URL parameter for pre-selecting a student
  useEffect(() => {
    // Check for "learnerId" (primary) or "student" (legacy)
    const learnerIdStr = searchParams.get("learnerId") || searchParams.get("student");
    const learnerId = learnerIdStr ? parseInt(learnerIdStr, 10) : null;

    if (learners.length > 0) {
      if (learnerId) {
        // Sync URL param to local state
        const learner = learners.find((l) => l.id === learnerId);
        
        // Only update state if needed to avoid loops
        if (learner && (!selectedLearner || selectedLearner.id !== learner.id)) {
          setSelectedLearner(learner);
        } else if (!learner) {
          // Invalid learner ID handling - gracefully reset
          if (selectedLearner) setSelectedLearner(null);
        }
      } else {
        // No learner selected in URL -> clear local state
        // This ensures back navigation to root works correctly
        if (selectedLearner) {
          setSelectedLearner(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, learners, selectedLearner]);

  useEffect(() => {
    if (selectedLearner) {
      if (canLoadHistory) {
        loadReviews();
      } else {
        setReviews([]);
      }
      loadSemesterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLearner, canLoadHistory]);

  const loadLearners = async () => {
    try {
      setLoading(true);
      setError(null);

      // Backend now handles filtering based on logged-in user (Admin/Berufsbildner)
      // and returns simplified DTO (id, firstName, lastName, professionType)
      const data = await semesterReviewService.getLearnersForSelection();
      
      const learnersList = Array.isArray(data) ? data : (data.users || []);

      learnersList.sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
          "de-CH"
        )
      );
      setLearners(learnersList);

    } catch (error) {
      console.error("Error loading learners:", error);
      setError("Error loading learners.");
    } finally {
      setLoading(false);
    }
  };
  
  const loadSemesterOptions = async () => {
    if (!selectedLearner) return;
    
    try {
      const options = await semesterReviewService.getSemesterOptions(selectedLearner.id);
      setSemesterOptions(options);

      // Default selection (current valid semester)
      // "HS until Feb 28" Logic
      // We calculate current desired "Code" (e.g., HS25) from today's date
      // and try to find it in options.
      
      if (options && options.length > 0) {
         const now = new Date();
         const month = now.getMonth(); // 0-11
         const year = now.getFullYear();
         
         // Logic:
         // If Month is 0 (Jan) or 1 (Feb), current semester is HS of (Year - 1).
         // If Month >= 2 (Mar) and < 7 (Aug), current semester is FS of Year.
         // Else (Aug-Dec), current semester is HS of Year.
         
         let targetCode = "";
         if (month === 0 || month === 1) { // Jan or Feb
             targetCode = `HS${(year - 1) % 100}`;
         } else if (month >= 2 && month < 7) { // Mar - Jul
             targetCode = `FS${year % 100}`;
         } else { // Aug - Dec
             targetCode = `HS${year % 100}`;
         }

         const found = options.find(o => o.semesterCode === targetCode);
         if (found) {
            setSelectedSemesterCode(targetCode);
         } else {
            // Default to last one? or first?
            // Usually last one is latest if sorted by date
            // The generation logic adds them in chronological order.
            // If future reviews pre-plan, maybe select last?
            // Safer: select last available option if "current" isn't found (maybe future start?)
            // Or select first?
            // Let's select the last one as default if current not found.
            setSelectedSemesterCode(options[options.length - 1].semesterCode);
         }
      }
    } catch (error) {
      console.error("Error loading semester options:", error);
    }
  };


  const loadReviews = async () => {
    if (!selectedLearner) return;

    try {
      setLoading(true);
      setError(null);
      const data = await semesterReviewService.getTrainerReviews({
        learnerId: selectedLearner.id,
      });
      setReviews(data || []);
    } catch (error) {
      console.error("Error loading reviews:", error);
      setError("Error loading reviews: " + (error.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async () => {
    if (!selectedLearner || !newReview.title.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    setError(null);

    try {
      setLoading(true);
      const reviewData = {
        learnerId: selectedLearner.id,
        templateCode: newReview.templateCode,
        title: newReview.title.trim(),
        status: "DRAFT",
      };

      const created = await semesterReviewService.createReview(reviewData);
      setShowCreateDialog(false);
      setNewReview({
        templateCode: "KOMPETENZEN",
        title: "",
      });

      navigate(`/semester-review/${created.id}`);
    } catch (error) {
      console.error("Error creating review:", error);
      setError("Error creating review: " + (error.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleImportPdf = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedLearner) {
      setError("Please select an apprentice first.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await semesterReviewService.importReviewFromPdf(file, selectedLearner.id);
      
      // Reload reviews to show the new one when this role can view history.
      if (canLoadHistory) {
        loadReviews();
      }
      
      // Optionally navigate to the review
      // navigate(`/semester-review/${importedReview.id}`);
    } catch (error) {
      console.error("Error importing PDF:", error);
      setError("Error importing PDF: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openCreateDialog = () => {
    if (!canCreate) {
      // Practice Instructors without CREATE_EDUCATIONAL_REPORTS can't create.
      return;
    }
    if (!selectedLearner) {
      setError("Please select an apprentice first.");
      return;
    }
    setError(null);

    const defaultTemplate = "KOMPETENZEN";
    const title = generateReviewTitle(defaultTemplate);
    
    setNewReview({
      templateCode: defaultTemplate,
      title: title,
    });

    setShowCreateDialog(true);
  };

  const handleEditClick = (review, e) => {
    e.stopPropagation();
    setEditingReview(review);
    setEditTitle(review.title);
    setShowEditDialog(true);
  };

  const handleUpdateReview = async () => {
    if (!editingReview || !editTitle.trim()) return;

    try {
      setLoading(true);
      await semesterReviewService.updateReviewDetails(editingReview.id, {
        title: editTitle.trim(),
      });
      setShowEditDialog(false);
      setEditingReview(null);
      setEditTitle("");
      loadReviews();
    } catch (error) {
      console.error("Error updating review:", error);
      setError("Error updating review: " + (error.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (review, e) => {
    e.stopPropagation();
    setReviewToDelete(review);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!reviewToDelete) return;

    try {
      await semesterReviewService.deleteReview(reviewToDelete.id);
      setError(null);
      setShowDeleteDialog(false);
      setReviewToDelete(null);
      loadReviews();
    } catch (error) {
      console.error("Error deleting review:", error);
      setError("Error deleting review: " + (error.message || ""));
      setShowDeleteDialog(false);
      setReviewToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setReviewToDelete(null);
  };

  // Immediately exports every visible review as one ZIP - the main, one-click action.
  const handleExportAll = async () => {
    if (!selectedLearner || isExporting || reviews.length === 0) return;
    try {
      setIsExporting(true);
      setExportError("");
      await semesterReviewService.exportLearnerReviewsAsZip(selectedLearner.id, null);
    } catch (err) {
      setExportError("Could not export reviews. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const openExportConfig = () => {
    setSelectedReviewIds(reviews.map((r) => r.id));
    setExportError("");
    setShowExportConfig(true);
  };

  const closeExportConfig = () => {
    if (isExporting) return;
    setShowExportConfig(false);
  };

  const toggleReviewSelected = (reviewId) => {
    setSelectedReviewIds((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const handleExportSelected = async () => {
    if (!selectedLearner || isExporting || selectedReviewIds.length === 0) return;
    try {
      setIsExporting(true);
      setExportError("");
      await semesterReviewService.exportLearnerReviewsAsZip(
        selectedLearner.id,
        selectedReviewIds
      );
      setShowExportConfig(false);
    } catch (err) {
      setExportError("Could not export the selected reviews. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const filteredLearners = learners.filter((learner) =>
    `${learner.firstName} ${learner.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Filter reviews by author type
  const filteredReviews = reviews.filter((review) => {
    if (authorFilter === "all") return true;

    // Use explicit role if available from backend (New Logic)
    if (review.authorRole) {
      if (authorFilter === "trainer") {
        return (
          review.authorRole === "TRAINER" ||
          review.authorRole === "ADMIN"
        );
      }
      if (authorFilter === "practice_instructor") {
        return review.authorRole === "PRACTICE_INSTRUCTOR";
      }
      if (authorFilter === "learner") {
        return review.authorRole === "LEARNER";
      }
    }

    // Fallback Legacy Logic (if backend hasn't returned roles yet)
    if (authorFilter === "trainer" || authorFilter === "practice_instructor") {
       // In legacy mode, we can't distinguish easily, so both might show generic "trainers"
       // Or we try to stick to the old 'not learner' logic for both, which is imperfect but safe
       return (
        review.authorName !==
        `${selectedLearner.firstName} ${selectedLearner.lastName}`
      );
    }
    if (authorFilter === "learner") {
      return (
        review.authorName ===
        `${selectedLearner.firstName} ${selectedLearner.lastName}`
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const base =
      "px-3 py-1 rounded-full text-xs md:text-sm font-semibold inline-flex items-center";

    const config = {
      DRAFT: {
        classes: `${base} bg-gray-100 text-gray-700 border border-gray-200`,
        label: "Draft",
        icon: "fas fa-pencil-alt",
      },
      IN_REVIEW: {
        classes: `${base} bg-blue-50 text-blue-800 border border-blue-200`,
        label: "In review",
        icon: "fas fa-hourglass-half",
      },
      COMPLETED: {
        classes: `${base} bg-green-50 text-green-800 border border-green-200`,
        label: "Completed",
        icon: "fas fa-check-circle",
      },
    };

    const cfg = config[status] || {
      classes: base + " bg-gray-100 text-gray-700",
      label: status,
      icon: "fas fa-tag",
    };

    return (
      <span className={cfg.classes}>
        <i className={`${cfg.icon} mr-2`}></i>
        {cfg.label}
      </span>
    );
  };

  const getTemplateLabel = (templateCode) => {
    const labelsDe = {
      KOMPETENZEN: "Bildungsbericht",
      BEWERTUNG_DER_AUSBILDUNG: "Bewertung der Ausbildung",
      BB_LEISTUNGSBEURTEILUNG: "Semestergespräch",
    };
    const labelsEn = {
      KOMPETENZEN: "Educational Report",
      BEWERTUNG_DER_AUSBILDUNG: "Evaluation of Training",
      BB_LEISTUNGSBEURTEILUNG: "Performance Review",
    };
    const labels = language === 'en' ? labelsEn : labelsDe;
    return labels[templateCode] || templateCode;
  };

  const getProfessionBadge = (professionType) => {
    if (!professionType) return null;

    const config = {
      INFORMATIK: {
        color: "bg-blue-100 text-blue-800 border-blue-200",
        icon: "fa-laptop-code",
      },
      MEDIAMATIK: {
        color: "bg-purple-100 text-purple-800 border-purple-200",
        icon: "fa-palette",
      },
      DETAILHANDEL: {
        color: "bg-orange-100 text-orange-600 border-orange-200",
        icon: "fas fa-store",
      },
      KUNDENDIALOG: {
        color: "bg-emerald-100 text-emerald-600 border-emerald-200",
        icon: "fas fa-headset",
      },
      KV: {
        color: "bg-gray-100 text-gray-600 border-gray-200",
        icon: "fas fa-file-invoice",
      },
    };

    const type = config[professionType] || {
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: "fa-briefcase",
      label: professionType,
    };

    return (
      <span
        className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${type.color} flex-shrink-0 inline-flex items-center gap-1.5 ml-2`}
        title={type.label}
      >
        <i className={`fas ${type.icon}`}></i>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-green-cloud py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas  fa-clipboard-check text-sunrise-red mr-4"></i>
                  Manage semester reviews
                </h1>
                <p className="text-sm md:text-base text-slate-cloud">
                  Select a apprentice, create new reviews and manage existing semester reviews.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle mr-3"></i>
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar: Learner selection */}
          <div className="lg:col-span-1">
            <div className="card-brand p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg md:text-xl font-bold text-grey-dusk">
                  Apprentices
                </h2>
                <button
                  onClick={loadLearners}
                  className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-2"
                  title="Reload apprentices"
                >
                  <i className="fas fa-sync-alt"></i>
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-grey-dusk mb-1">
                  Search
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-search text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-brand w-full pl-9"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <i className="fas fa-times text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {filteredLearners.map((learner) => (
                  <button
                    key={learner.id}
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set("learnerId", learner.id);
                      if (newParams.has("student")) {
                        newParams.delete("student");
                      }
                      setSearchParams(newParams);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all border ${
                      selectedLearner?.id === learner.id
                        ? "bg-sunrise-red text-white border-sunrise-red shadow-brand"
                        : "bg-ice-white border-pale-dawn hover:bg-pale-dawn/40 text-grey-dusk"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">
                        {learner.firstName} {learner.lastName}
                      </span>
                      {getProfessionBadge(learner.professionType)}
                    </div>
                  </button>
                ))}

                {filteredLearners.length === 0 && (
                  <p className="text-center text-gray-500 py-4 text-sm">
                    No learners found.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right side: Reviews and actions */}
          <div className="lg:col-span-2">
            {selectedLearner ? (
              <div className="card-brand p-6 md:p-7">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-grey-dusk">
                      {selectedLearner.firstName} {selectedLearner.lastName}
                    </h2>
                    <p className="text-sm text-gray-500">
                      Semester reviews and training feedback for this apprentice.
                    </p>
                    {exportError && (
                      <p className="text-sm text-red-600 mt-1">
                        <i className="fas fa-exclamation-circle mr-1"></i>
                        {exportError}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {canLoadHistory && (
                      <div className="flex rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                        <button
                          onClick={handleExportAll}
                          disabled={isExporting || reviews.length === 0}
                          title="Download all reviews as a ZIP archive"
                          className="px-3 py-2 bg-white text-slate-700 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isExporting ? (
                            <i className="fas fa-circle-notch fa-spin"></i>
                          ) : (
                            <i className="fas fa-file-zipper"></i>
                          )}
                          Export all reports
                        </button>
                        <button
                          onClick={openExportConfig}
                          disabled={isExporting || reviews.length === 0}
                          title="Choose which reports to export"
                          aria-label="Choose which reports to export"
                          className="px-2.5 py-2 bg-white text-slate-500 border-l border-slate-200 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <i className="fas fa-sliders"></i>
                        </button>
                      </div>
                    )}

                    {/* PIs see this only when they may create educational reports;
                        their template selector is limited to Bildungsbericht. */}
                    {canCreate && (
                      <>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImportPdf}
                          accept=".pdf"
                          className="hidden"
                        />
                        <button
                          onClick={openCreateDialog}
                          className="bg-sunrise-red hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center text-sm shadow-sm"
                        >
                          <i className="fas fa-plus mr-2"></i>
                          {isPracticeInstructor
                            ? language === "en"
                              ? "New report"
                              : "New Review"
                            : "New review"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {canLoadHistory ? (
                  <>
                    <div className="inline-flex bg-ice-white rounded-xl p-1 mb-4 border border-pale-dawn">
                      <button
                        onClick={() => setAuthorFilter("all")}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                          authorFilter === "all"
                            ? "bg-sunrise-red text-white shadow-sm"
                            : "text-grey-dusk hover:bg-pale-dawn/50"
                        }`}
                      >
                        <i className="fas fa-list mr-1.5"></i>
                        All
                      </button>
                      <button
                        onClick={() => setAuthorFilter("trainer")}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                          authorFilter === "trainer"
                            ? "bg-sunrise-red text-white shadow-sm"
                            : "text-grey-dusk hover:bg-pale-dawn/50"
                        }`}
                      >
                        <i className="fas fa-chalkboard-teacher mr-1.5"></i>
                        Berufsbildner
                      </button>
                      <button
                        onClick={() => setAuthorFilter("practice_instructor")}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                          authorFilter === "practice_instructor"
                            ? "bg-sunrise-red text-white shadow-sm"
                            : "text-grey-dusk hover:bg-pale-dawn/50"
                        }`}
                      >
                        <i className="fas fa-briefcase mr-1.5"></i>
                        Practical Trainer
                      </button>
                      <button
                        onClick={() => setAuthorFilter("learner")}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                          authorFilter === "learner"
                            ? "bg-sunrise-red text-white shadow-sm"
                            : "text-grey-dusk hover:bg-pale-dawn/50"
                        }`}
                      >
                        <i className="fas fa-user-graduate mr-1.5"></i>
                        Apprentice
                      </button>
                    </div>
                    <div className="space-y-4">
                      {filteredReviews.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <div className="w-20 h-20 bg-ice-white rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-inbox text-2xl text-gray-400"></i>
                          </div>
                          <p className="text-sm md:text-base">
                            {reviews.length === 0
                              ? "No visible reviews or educational reports for this learner yet."
                              : "No matching reviews found."}
                          </p>
                        </div>
                      ) : (
                        filteredReviews.map((review) => (
                          <div
                            key={review.id}
                            className="border border-pale-dawn rounded-xl bg-gradient-to-r from-ice-white to-pale-dawn/50 hover:shadow-brand-lg transition-all cursor-pointer"
                            onClick={() =>
                              navigate(`/semester-review/${review.id}`)
                            }
                          >
                            <div className="p-4 md:p-5">
                              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-bold text-grey-dusk truncate">
                                    {review.title || "Untitled review"}
                                  </h3>
                                  <p className="text-xs md:text-sm text-gray-600">
                                    {getTemplateLabel(review.templateCode)}
                                  </p>
                                  <p className="text-xs text-slate-cloud mt-1">
                                    <i className="fas fa-user-edit mr-1"></i>
                                    Created by: {review.authorName}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 md:ml-4">
                                  {getStatusBadge(review.status)}
                                  {(currentUser?.roles?.some(
                                    (r) => r.name === "ADMIN"
                                  ) ||
                                    (review.authorId === currentUser?.id &&
                                      review.status === "DRAFT")) && (
                                    <>
                                      <button
                                        onClick={(e) => handleEditClick(review, e)}
                                        className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                                        title="Edit title"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        onClick={(e) =>
                                          handleDeleteClick(review, e)
                                        }
                                        className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                        title="Delete review"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs md:text-sm text-gray-600 border-t border-pale-dawn/70 pt-3">
                                <div>
                                  <span className="font-medium">Created:</span>{" "}
                                  {review.createdAt
                                    ? new Date(review.createdAt).toLocaleDateString(
                                        "de-CH"
                                      )
                                    : "-"}
                                </div>
                                {review.completedAt && (
                                  <div>
                                    <span className="font-medium">Completed:</span>{" "}
                                    {new Date(
                                      review.completedAt
                                    ).toLocaleDateString("de-CH")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-pale-dawn bg-ice-white px-5 py-8 text-center text-slate-cloud">
                    Past semester meetings and educational report history are hidden for this practice instructor.
                  </div>
                )}
              </div>
            ) : (
              <div className="card-brand p-10 md:p-12 text-center">
                <div className="w-24 h-24 bg-ice-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-arrow-left text-3xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 text-sm md:text-lg">
                  Select an apprentice on the left to manage their semester reviews.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Review Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-lg w-full relative">
            <button
              onClick={() => {
                setShowCreateDialog(false);
                setError(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times text-lg"></i>
            </button>

            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl md:text-2xl font-bold text-grey-dusk flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-sunrise-red/10 flex items-center justify-center mr-3">
                    <i className="fas fa-plus text-sunrise-red"></i>
                  </span>
                  Create new semester review
                </h2>
                <div className="mr-8">
                   <LanguageSwitcher />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Create a new semester review for{" "}
                <strong>
                  {selectedLearner?.firstName} {selectedLearner?.lastName}
                </strong>
                .
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    Template
                  </label>
                  <select
                    value={newReview.templateCode}
                    onChange={(e) => {
                      const newTemplateCode = e.target.value;
                      const title = generateReviewTitle(newTemplateCode, selectedSemesterCode);
                      setNewReview({
                        templateCode: newTemplateCode,
                        title: title,
                      });
                    }}
                    className="input-brand w-full"
                  >
                    <option value="KOMPETENZEN">{language === 'en' ? 'Competency Report' : 'Bildungsbericht'}</option>
                    {!isPracticeInstructor && (
                      <option value="BB_LEISTUNGSBEURTEILUNG">{language === 'en' ? 'Performance Assessment' : 'Semestergespräch'}</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    Semester
                  </label>
                  <select
                    value={selectedSemesterCode}
                    onChange={(e) => {
                      const newCode = e.target.value;
                      setSelectedSemesterCode(newCode);
                      const title = generateReviewTitle(newReview.templateCode, newCode);
                      setNewReview(prev => ({ ...prev, title }));
                    }}
                    className="input-brand w-full"
                  >
                    {semesterOptions.map((opt) => (
                      <option key={opt.semesterCode} value={opt.semesterCode}>
                        {opt.displayShort}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-dusk mb-2">
                    {language === 'en' ? 'Title' : 'Titel'}
                  </label>
                  <input
                    type="text"
                    value={newReview.title}
                    onChange={(e) =>
                      setNewReview({ ...newReview, title: e.target.value })
                    }
                    className="input-brand w-full"
                    placeholder={language === 'en' ? "e.g. Semester review Fall 2025" : "z.B. Semestergespräch HS25"}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                    <i className="fas fa-exclamation-circle mr-2"></i>
                    {error}
                  </div>
                )}
              </div>

              <div className="flex flex-row gap-3 mt-6">
                {/* Cancel */}
                <button
                  onClick={() => {
                    setShowCreateDialog(false);
                    setError(null);
                  }}
                  disabled={loading}
                  className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 
               transition-colors text-sm font-medium flex items-center justify-center"
                >
                  Cancel
                </button>

                {/* Create */}
                <button
                  onClick={handleCreateReview}
                  disabled={loading}
                  className="flex-1 px-6 py-3 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white 
               transition-colors text-sm font-semibold flex items-center justify-center"
                >
                  {loading ? "Creating..." : "Create review"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Review Dialog */}
      {showEditDialog && editingReview && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-lg w-full p-6 md:p-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <i className="fas fa-edit text-blue-600 text-xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-grey-dusk">
                Edit Review
              </h2>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-pale-dawn focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red transition-all"
                placeholder="Review title"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingReview(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateReview}
                className="flex-1 px-6 py-3 bg-sunrise-red text-white rounded-xl hover:bg-sunrise-600 transition-all shadow-md hover:shadow-lg font-medium inline-flex items-center justify-center"
              >
                <i className="fas fa-check mr-2"></i>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && reviewToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="card-brand max-w-md w-full p-6 md:p-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-grey-dusk">
                Delete review?
              </h2>
            </div>

            <p className="text-sm md:text-base text-gray-600 mb-6">
              Do you really want to delete the review{" "}
              <strong>"{reviewToDelete.title}"</strong>? This action cannot be
              undone.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={cancelDelete} className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center">
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-6 py-3 bg-sunrise-red text-white rounded-xl hover:bg-sunrise-600 transition-all shadow-md hover:shadow-lg font-medium inline-flex items-center justify-center"
              >
                <i className="fas fa-trash mr-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Configuration Modal */}
      <Modal
        isOpen={showExportConfig}
        onClose={closeExportConfig}
        title="Export reports"
        subtitle="Select which reports should be included."
        icon="fa-file-zipper"
        size="md"
        closeOnBackdrop={!isExporting}
        footer={
          <>
            <ModalSecondaryButton onClick={closeExportConfig} disabled={isExporting}>
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              onClick={handleExportSelected}
              disabled={selectedReviewIds.length === 0}
              loading={isExporting}
              loadingText="Preparing ZIP..."
            >
              Export {selectedReviewIds.length}{" "}
              {selectedReviewIds.length === 1 ? "report" : "reports"}
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-slate-cloud mb-4">
          Each selected report will be downloaded as a separate file inside one ZIP
          archive.
        </p>

        {reviews.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            No reports available to export.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => setSelectedReviewIds(reviews.map((r) => r.id))}
                  className="font-semibold text-sunrise-red hover:underline"
                >
                  Select all
                </button>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedReviewIds([])}
                  className="font-semibold text-sunrise-red hover:underline"
                >
                  Deselect all
                </button>
              </div>
              <span className="text-xs text-slate-500">
                {selectedReviewIds.length} of {reviews.length} selected
              </span>
            </div>

            <div className="space-y-1 max-h-72 overflow-y-auto border border-pale-dawn rounded-xl p-2">
              {reviews.map((review) => (
                <label
                  key={review.id}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedReviewIds.includes(review.id)}
                    onChange={() => toggleReviewSelected(review.id)}
                    className="w-4 h-4 accent-sunrise-red rounded"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {review.title || "Untitled review"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {getTemplateLabel(review.templateCode)}
                      {review.createdAt
                        ? ` • ${new Date(review.createdAt).toLocaleDateString("de-CH")}`
                        : ""}
                      {review.authorName ? ` • ${review.authorName}` : ""}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TrainerSemesterReviewsPage;
